import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSticker, ShapeType, SHAPE_IMAGES } from '../context/StickerContext';
import { UploadDropzone } from '../components/ui/UploadDropzone';
import { PrimaryButton, SecondaryButtonOutline } from '../components/ui/Buttons';
import { StepProgress } from '../components/ui/StepProgress';
import { Info, Loader2 } from 'lucide-react';
import { callWFAPI, uploadImage, reloadBuilder } from '../api';
const SHAPES: ShapeType[] = ['Circle', 'Square', 'Oval', 'Die Cut', 'Rounded Corners', 'Rectangle'];
const PRICE_TIERS = [{
  label: '1–250',
  rate: '$0.040'
}, {
  label: '251–500',
  rate: '$0.035'
}, {
  label: '501–750',
  rate: '$0.025'
}, {
  label: '751–1,000',
  rate: '$0.020'
}, {
  label: '1,001+',
  rate: '$0.018'
}];
function fmt(n: number) {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  });
}
export function StickersPage() {
  const navigate = useNavigate();
  const {
    quantity,
    setQuantity,
    width,
    setWidth,
    height,
    setHeight,
    shape,
    setShape,
    cutIndividually,
    setCutIndividually,
    uploadState,
    uploadedFile,
    squareInches,
    rate,
    tierLabel,
    baseStickerPrice,
    cutFee,
    totalPrice,
    pricePerSticker,
    sessionKey,
    imagePath,
    setImagePath,
    editCartProductMode
  } = useSticker();
  const [isGenerating, setIsGenerating] = useState(false);
  const isValid = uploadState === 'loaded' && quantity > 0 && Number.isInteger(quantity) && width > 0 && height > 0;
  const handleGenerateProof = async () => {
    setIsGenerating(true);
    try {
      if (sessionKey) {
        let finalImagePath = imagePath;
        // In edit cart product mode, re-upload the image by URL only if user hasn't uploaded a new file
        if (editCartProductMode && !uploadedFile?.file && uploadedFile?.thumbnail) {
          try {
            const newPath = await uploadImage(sessionKey, uploadedFile.thumbnail, uploadedFile.name || 'cart-image');
            finalImagePath = newPath;
            setImagePath(newPath);
          } catch {
            // If re-upload fails, continue with existing imagePath
          }
        }
        await callWFAPI(0, 'storeSessionData', {
          cookie: 1,
          data: JSON.stringify({
            shape,
            quantity,
            width,
            height,
            cutIndividually,
            imagePath: finalImagePath
          })
        });
      }
      navigate('/stickers/proof');
    } catch {
      // Silently ignore errors
    } finally {
      setIsGenerating(false);
    }
  };
  return <div className="min-h-screen bg-[#E1E1E1]" data-id="element-643">
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-644">
        <div className="mb-6 text-center" data-id="element-645">
          <h1 className="text-3xl font-bold text-[#111111] mb-2" data-id="element-646">
            Custom Stickers
          </h1>
          <p className="text-[#5F5F5F] text-base max-w-4xl mx-auto" data-id="element-647">
            Upload your artwork, choose your shape, enter your exact size and
            quantity, and preview your sticker instantly.
          </p>
        </div>

        <StepProgress currentStep={1} data-id="element-648" />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-id="element-649">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-3 space-y-6" data-id="element-650">
            <div className="bg-white rounded-xl shadow-sm border border-[#D9D9D9] p-6" data-id="element-651">
              <h2 className="text-lg font-semibold text-[#111111] mb-6" data-id="element-652">
                Configuration
              </h2>

              {/* Shape */}
              <div className="mb-8" data-id="element-653">
                <label className="text-sm font-semibold text-[#111111] block mb-3" data-id="element-654">
                  Shape
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-655">
                  {SHAPES.map(s => {
                  const isSelected = shape === s;
                  return <button key={s} onClick={() => setShape(s)} className={`relative rounded-xl border-2 transition-all text-center ${isSelected ? 'border-[#008696] ring-2 ring-[#EAF6F7] bg-[#EAF6F7]' : 'border-[#D9D9D9] hover:border-gray-400 bg-gray-100'}`} data-id="element-656">
                        <div className="px-3 pt-3 pb-1" data-id="element-657">
                          <img src={SHAPE_IMAGES[s]} alt={s} className="w-full h-28 object-contain drop-shadow-sm" onError={e => {
                        ;
                        (e.target as HTMLImageElement).style.display = 'none';
                      }} data-id="element-658" />
                        </div>
                        <div className={`px-3 py-2 text-xs font-semibold ${isSelected ? 'text-[#008696]' : 'text-[#5F5F5F]'}`} data-id="element-659">
                          {s}
                        </div>
                      </button>;
                })}
                </div>
                {shape === 'Die Cut' && <p className="text-xs text-[#008696] bg-[#EAF6F7] rounded-lg px-3 py-2 mt-3" data-id="element-660">
                    Die Cut stickers are cut to the exact outline of your
                    uploaded artwork.
                  </p>}
              </div>

              <div className="border-t border-[#D9D9D9] mb-8" data-id="element-661" />

              {/* Quantity */}
              <div className="mb-8" data-id="element-662">
                <label className="text-sm font-semibold text-[#111111] block mb-1" data-id="element-663">
                  Quantity
                </label>
                <p className="text-xs text-[#5F5F5F] mb-3" data-id="element-664">
                  Enter the exact quantity you want.
                </p>
                <input type="number" min={1} step={1} value={quantity || ''} placeholder="e.g. 100" onChange={e => {
                const val = parseInt(e.target.value, 10);
                setQuantity(isNaN(val) ? 0 : val);
              }} className="w-full rounded-lg border border-[#D9D9D9] px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#008696] focus:border-[#008696] outline-none" data-id="element-665" />
                {quantity > 0 && !Number.isInteger(quantity) && <p className="text-xs text-[#FF0000] mt-1" data-id="element-666">
                    Quantity must be a whole number.
                  </p>}
                {quantity > 0 && <p className="text-xs text-[#5F5F5F] mt-1" data-id="element-667">
                    Tier:{' '}
                    <span className="font-medium text-[#111111]" data-id="element-668">
                      {tierLabel}
                    </span>{' '}
                    — ${rate.toFixed(3)}/sq in
                  </p>}
              </div>

              <div className="border-t border-[#D9D9D9] mb-8" data-id="element-669" />

              {/* Size */}
              <div className="mb-8" data-id="element-670">
                <label className="text-sm font-semibold text-[#111111] block mb-1" data-id="element-671">
                  Sticker Size
                </label>
                <p className="text-xs text-[#5F5F5F] mb-3" data-id="element-672">
                  Enter your finished sticker size in inches. Size includes the
                  cut line or shape of your sticker.
                </p>
                <div className="grid grid-cols-2 gap-4" data-id="element-673">
                  <div data-id="element-674">
                    <label className="text-xs font-medium text-[#5F5F5F] block mb-1" data-id="element-675">
                      Width
                    </label>
                    <div className="relative" data-id="element-676">
                      <input type="number" min={0.1} max={50} step={0.1} value={width || ''} placeholder="e.g. 3.5" onChange={e => {
                      const val = parseFloat(e.target.value) || 0;
                      setWidth(val > 50 ? 50 : val);
                    }} className="w-full rounded-lg border border-[#D9D9D9] px-4 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-[#008696] focus:border-[#008696] outline-none" data-id="element-677" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#5F5F5F] font-medium" data-id="element-678">
                        in
                      </span>
                    </div>
                    {width > 50 && <p className="text-xs text-[#FF0000] mt-1" data-id="element-679">
                        Maximum width is 50 inches.
                      </p>}
                  </div>
                  <div data-id="element-680">
                    <label className="text-xs font-medium text-[#5F5F5F] block mb-1" data-id="element-681">
                      Height
                    </label>
                    <div className="relative" data-id="element-682">
                      <input type="number" min={0.1} step={0.1} value={height || ''} placeholder="e.g. 3.5" onChange={e => setHeight(parseFloat(e.target.value) || 0)} className="w-full rounded-lg border border-[#D9D9D9] px-4 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-[#008696] focus:border-[#008696] outline-none" data-id="element-683" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#5F5F5F] font-medium" data-id="element-684">
                        in
                      </span>
                    </div>
                  </div>
                </div>
                {width > 0 && height > 0 && <p className="text-xs text-[#5F5F5F] mt-2" data-id="element-685">
                    {width} × {height} ={' '}
                    <span className="font-medium text-[#111111]" data-id="element-686">
                      {squareInches.toFixed(2)} sq in
                    </span>{' '}
                    per sticker
                  </p>}
              </div>

              <div className="border-t border-[#D9D9D9] mb-8" data-id="element-687" />

              {/* Cut Option */}
              <div className="mb-8" data-id="element-688">
                <label className="text-sm font-semibold text-[#111111] block mb-1" data-id="element-689">
                  Cut Option
                </label>
                <p className="text-sm text-[#5F5F5F] mb-3" data-id="element-690">
                  Cut the sticker individually for me?
                </p>
                <div className="bg-gray-100 p-1 rounded-full flex" data-id="element-691">
                  <button onClick={() => setCutIndividually(true)} className={`flex-1 py-2 px-4 text-sm font-medium rounded-full transition-all duration-200 ${cutIndividually ? 'bg-[#008696] text-white shadow-sm' : 'text-[#5F5F5F] hover:text-[#111111]'}`} data-id="element-692">
                    Yes
                  </button>
                  <button onClick={() => setCutIndividually(false)} className={`flex-1 py-2 px-4 text-sm font-medium rounded-full transition-all duration-200 ${!cutIndividually ? 'bg-[#008696] text-white shadow-sm' : 'text-[#5F5F5F] hover:text-[#111111]'}`} data-id="element-693">
                    No
                  </button>
                </div>
                <p className="text-xs text-[#5F5F5F] mt-2" data-id="element-694">
                  Yes adds a one-time $15.00 finishing fee.
                </p>
              </div>

              <div className="border-t border-[#D9D9D9] mb-6" data-id="element-695" />

              {/* Included Product Details */}
              <div data-id="element-696">
                <div className="flex items-center gap-2 mb-3" data-id="element-697">
                  <Info size={14} className="text-[#5F5F5F]" data-id="element-698" />
                  <label className="text-sm font-semibold text-[#111111]" data-id="element-699">
                    Included Product Details
                  </label>
                </div>
                <div className="bg-[#EAF6F7] rounded-xl p-4 space-y-2" data-id="element-700">
                  <div className="flex items-center gap-2" data-id="element-701">
                    <span className="w-2 h-2 rounded-full bg-[#008696] shrink-0" data-id="element-702" />
                    <span className="text-sm text-[#111111]" data-id="element-703">
                      <span className="font-medium" data-id="element-704">Finish:</span> Gloss
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-705">
                    <span className="w-2 h-2 rounded-full bg-[#008696] shrink-0" data-id="element-706" />
                    <span className="text-sm text-[#111111]" data-id="element-707">
                      <span className="font-medium" data-id="element-708">Material:</span> 4.3 mil air
                      release vinyl
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-709">
                    <span className="w-2 h-2 rounded-full bg-[#008696] shrink-0" data-id="element-710" />
                    <span className="text-sm text-[#111111]" data-id="element-711">
                      <span className="font-medium" data-id="element-712">Print method:</span>{' '}
                      Eco-solvent printed
                    </span>
                  </div>
                  <p className="text-xs text-[#5F5F5F] pt-1" data-id="element-713">
                    Custom printed stickers on 4.3 mil air release vinyl with
                    gloss finish included.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="lg:col-span-2 space-y-6" data-id="element-714">
            <div className="bg-white rounded-xl shadow-sm border border-[#D9D9D9] p-6" data-id="element-715">
              <h2 className="text-lg font-semibold text-[#111111] mb-4" data-id="element-716">
                Upload Your Design
              </h2>
              <UploadDropzone data-id="element-717" />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-[#D9D9D9] p-6 sticky top-6" data-id="element-718">
              <h2 className="text-lg font-semibold text-[#111111] mb-4" data-id="element-719">
                Live Price
              </h2>
              <div className="space-y-2 text-sm" data-id="element-720">
                {[{
                l: 'Shape',
                v: shape
              }, {
                l: 'Quantity',
                v: quantity > 0 ? quantity.toLocaleString() : '—'
              }, {
                l: 'Width',
                v: width > 0 ? `${width} in` : '—'
              }, {
                l: 'Height',
                v: height > 0 ? `${height} in` : '—'
              }, {
                l: 'Sq in per sticker',
                v: squareInches > 0 ? squareInches.toFixed(2) : '—'
              }].map(({
                l,
                v
              }) => <div key={l} className="flex justify-between" data-id="element-721">
                    <span className="text-[#5F5F5F]" data-id="element-722">{l}</span>
                    <span className="font-medium text-[#111111]" data-id="element-723">{v}</span>
                  </div>)}

                <div className="border-t border-[#D9D9D9] my-3" data-id="element-724" />

                {[{
                l: 'Pricing tier',
                v: quantity > 0 ? tierLabel : '—'
              }, {
                l: 'Rate / sq in',
                v: quantity > 0 ? `$${rate.toFixed(3)}` : '—'
              }, {
                l: 'Base sticker price',
                v: baseStickerPrice > 0 ? fmt(baseStickerPrice) : '—'
              }].map(({
                l,
                v
              }) => <div key={l} className="flex justify-between" data-id="element-725">
                    <span className="text-[#5F5F5F]" data-id="element-726">{l}</span>
                    <span className="font-medium text-[#111111]" data-id="element-727">{v}</span>
                  </div>)}
                <div className="flex justify-between" data-id="element-728">
                  <span className="text-[#5F5F5F]" data-id="element-729">Individual cut fee</span>
                  <span className={`font-medium ${cutFee > 0 ? 'text-[#111111]' : 'text-[#5F5F5F]'}`} data-id="element-730">
                    {cutFee > 0 ? fmt(cutFee) : '$0.00'}
                  </span>
                </div>

                <div className="border-t border-[#D9D9D9] my-3" data-id="element-731" />

                <div className="flex justify-between items-center" data-id="element-732">
                  <span className="text-base font-bold text-[#111111]" data-id="element-733">
                    Total
                  </span>
                  <span className="text-2xl font-bold text-[#111111]" data-id="element-734">
                    {totalPrice > 0 ? fmt(totalPrice) : '—'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-735">
                  <span className="text-[#5F5F5F] text-xs" data-id="element-736">
                    Price per sticker
                  </span>
                  <span className="text-xs font-medium text-[#5F5F5F]" data-id="element-737">
                    {pricePerSticker > 0 ? fmt(pricePerSticker) : '—'}
                  </span>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-[#D9D9D9]" data-id="element-738">
                <p className="text-xs font-semibold text-[#5F5F5F] uppercase tracking-wide mb-2" data-id="element-739">
                  Pricing Tiers
                </p>
                <div className="space-y-1" data-id="element-740">
                  {PRICE_TIERS.map(tier => {
                  const isActive = quantity > 0 && tier.label === tierLabel;
                  return <div key={tier.label} className={`flex justify-between text-xs px-2 py-1 rounded ${isActive ? 'bg-[#EAF6F7] text-[#008696] font-semibold' : 'text-[#5F5F5F]'}`} data-id="element-741">
                        <span data-id="element-742">{tier.label}</span>
                        <span data-id="element-743">{tier.rate} / sq in</span>
                      </div>;
                })}
                </div>
                <p className="text-xs text-[#FF0000] mt-3" data-id="element-744">
                  Pricing Tiers are based on single images, you can not upload 2
                  or more images in one file and expect us to split the quantity
                  per the images.
                </p>
              </div>

              <div className="mt-6" data-id="element-745">
                <div className="flex gap-3" data-id="element-746">
                  {editCartProductMode && <SecondaryButtonOutline className="flex-1" onClick={() => reloadBuilder(navigate)} data-id="element-747">
                      Cancel
                    </SecondaryButtonOutline>}
                  <PrimaryButton className="flex-1" disabled={!isValid || isGenerating} onClick={handleGenerateProof} data-id="element-748">
                    {isGenerating && <Loader2 size={16} className="animate-spin mr-2" data-id="element-749" />}
                    Generate Proof
                  </PrimaryButton>
                </div>
                {!isValid && !isGenerating && <p className="text-xs text-[#5F5F5F] text-center mt-2" data-id="element-750">
                    Upload a file and fill in all fields to continue.
                  </p>}
              </div>

              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-id="element-751">
                <p className="text-xs text-amber-800 leading-relaxed" data-id="element-752">
                  <span className="font-semibold" data-id="element-753">Pricing Notice:</span> Sticker
                  pricing is already set at wholesale rates. Additional
                  wholesale account discounts do not apply to sticker orders, as
                  these prices already reflect significant savings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>;
}